import { normalizeSortResult } from "@ubnt/store.core/lib/utils/array";
import { DisplayProductStatus } from "types";
import {
  Language,
  StoreId,
  UnionToAllPossibleTuples,
} from "@ubnt/store.core/lib/types";

import {
  sortByTagWeight,
  SortSiteId,
} from "@ubnt/store.core/lib/utils/product";
import { combineSorts } from "./array";

/**
 * Removes the regional (and other) SKU postfix from the SKU.
 */
export const REGIONAL_SKU_POSTFIX = [
  " EU",
  "-EU",
  "(EU)",
  " US",
  "(US)",
  "-US",
  "-BR",
  "-CN",
  "-TW",
  "-JP",
  "-AU",
  "-AR",
  "-UK",
  "-U",
  "-IND",
  "-Standalone",
];

export const removeRegionalSkuPostfix = (sku?: string) => {
  const trimmedSku = sku?.trim();

  if (!trimmedSku) return undefined;

  const postfix = REGIONAL_SKU_POSTFIX.find((postfix) =>
    trimmedSku.trim().toUpperCase().endsWith(postfix.toUpperCase()),
  );

  if (!postfix) return trimmedSku;

  return trimmedSku.slice(0, -postfix.length).trim();
};

export const UI_CARE_SKU_PREFIX = "UICARE-";
/**
 * Util to check if the UI Care is meant for the given product
 */
export const isUiCareMatch = ({
  variant,
  uiCare,
}: {
  variant: { sku: string };
  uiCare: { sku: string };
}) => {
  if (!uiCare.sku.startsWith(UI_CARE_SKU_PREFIX)) {
    return false;
  }

  // Extract the target SKU from UI Care
  const targetSku = uiCare.sku.substring(UI_CARE_SKU_PREFIX.length);

  /**
   * HACK: To to improve matching attempting to remove regionality postfix as currently some uicare product SKUs don't
   * have a region while the target product has. As changing the SKU requires a NS product change, we currently just
   * strip them out.
   * FIXME: This is not a perfect solution as if a product varies just be EU/UK versions like for amplifi devices then
   * we might target the incorrect target product. But as we currently don't need that scenario and we need a quick fix,
   * this will be sufficient
   */
  const targetSkuNormalised =
    removeRegionalSkuPostfix(targetSku)?.toLocaleLowerCase();
  const variantSkuNormalised = removeRegionalSkuPostfix(
    variant.sku,
  )?.toLocaleLowerCase();

  // Something went wrong with the target SKU acquisition
  if (!targetSkuNormalised || !variantSkuNormalised) {
    return false;
  }

  return variantSkuNormalised === targetSkuNormalised;
};

export const sortByAvailabilityDesc = (current, next): number => {
  return normalizeSortResult(
    getProductStatusPriority(next) - getProductStatusPriority(current),
  );
};

/**
 * Variants statuses sorted in ascending order from least preferable to most, so if some new status comes in, then the
 * lookup for `.indexOf()` would return `-1`, thus being "worse" then DisplayProductStatus.SoldOut and moved to the very
 * back of list
 */
const sortedStatusesByPriorityAsc: UnionToAllPossibleTuples<DisplayProductStatus> =
  [
    DisplayProductStatus.Unavailable,
    DisplayProductStatus.SoldOut,
    DisplayProductStatus.ComingSoon,
    DisplayProductStatus.RegionNotAvailable,
    DisplayProductStatus.Backorder,
    DisplayProductStatus.Available,
  ];
const getProductStatusPriority = (product): number => {
  return sortedStatusesByPriorityAsc.indexOf(product.status);
};

/**
 * Sorts products based on the given sort type.
 * @param currentSort - sort type
 * @param sortByWeightTag - if false, then sort by weight tag is
 *                          ignored (e.g. for `Related Add-ons` where
 *                          we don't want to sort by weight tag)
 */
export const sortProducts =
  (storeId?: StoreId, sortByWeightTag = true) =>
  (current, next) => {
    const sorts = [sortByAvailabilityDesc];

    // Add sorting by tag weight
    if (sortByWeightTag) {
      sorts.push(sortByTagWeight(SortSiteId.Store, storeId));
    }

    // Do additional sorting by tag weight only after initial sorting resulting in "same group"
    return combineSorts(sorts)(current, next);
  };

// Handle deprecated specifically localized languages
export const resolveDeprecatedLang = (lang: string, storeId: string) => {
  if (lang === "es") return storeId === "eu" ? Language.EsEs : Language.EsMx;
  if (lang === "pt") return storeId === "eu" ? Language.PtPt : Language.PtBr;
  return null;
};

import {
  enUS,
  ar,
  de,
  el,
  fr,
  it,
  ja,
  zhCN,
  zhTW,
  ca,
  cs,
  da,
  nl,
  nn,
  ko,
  pl,
  pt,
  ptBR,
  ru,
  es,
  sv,
  tr,
} from "date-fns/locale";

// Map to assign languages to locales for date-fns
export const LOCALE_BY_LANGUAGE = {
  [Language.En]: enUS,
  [Language.Ar]: ar,
  [Language.De]: de,
  [Language.El]: el,
  [Language.Fr]: fr,
  [Language.It]: it,
  [Language.Ja]: ja,
  [Language.Zh]: zhCN,
  [Language.Tw]: zhTW,
  [Language.Ca]: ca,
  [Language.Cs]: cs,
  [Language.Da]: da,
  [Language.Nl]: nl,
  [Language.Ko]: ko,
  [Language.No]: nn,
  [Language.Pl]: pl,
  [Language.PtBr]: ptBR,
  [Language.PtPt]: pt,
  [Language.Ru]: ru,
  [Language.EsMx]: es,
  [Language.EsEs]: es,
  [Language.Sv]: sv,
  [Language.Tr]: tr,
};
